/**
 * ThemeV2SettingsPanel - Main settings panel for V2 theme editor
 *
 * Renders all V2 theme settings sections as SAccordion panels:
 * - V2ColorsSection (ThemeV2Colors)
 * - V2FontsSection (ThemeV2Fonts with TypographyStyle)
 * - V2OptionsListSection (ThemeV2OptionsListSettings)
 * - V2BackdropSection (Backdrop — solid/photo/clip background)
 * - V2HeaderBarSection (HeaderBar — back/skip/progress)
 * - V2ActionBarSection (ActionBar — primary/secondary buttons)
 *
 * Background/HeaderBar/ActionBar sections reuse V2 editor sections from
 * reactFlow/editorSectionsV2 via a bridge pattern:
 * - A mock FlowStepNode wraps theme values at their expected content.* paths
 * - onChange paths are translated back to theme-level paths
 */

import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { V2ColorsSection } from './sections/V2ColorsSection';
import { V2FontsSection } from './sections/V2FontsSection';
import { V2OptionsListSection } from './sections/V2OptionsListSection';
import { V2AnimationsSection } from './sections/V2AnimationsSection';
import { V2BackdropSection } from '@modules/reactFlow/components/onboarding/editorSectionsV2/V2BackdropSection';
import { V2HeaderBarSection } from '@modules/reactFlow/components/onboarding/editorSectionsV2/V2HeaderBarSection';
import { V2ActionBarSection } from '@modules/reactFlow/components/onboarding/editorSectionsV2/V2ActionBarSection';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { ThemeV2 } from '../types/themesV2.types';

export interface ThemeV2SettingsPanelProps {
  /** V2 Theme data to edit */
  theme: ThemeV2;
  /** Callback when any setting changes - receives path and value */
  onChange: (path: string, value: unknown) => void;
  /** Whether panel is disabled (during save) */
  disabled?: boolean;
  /** Optional transition demo strip rendered inside the Animations section */
  animationsDemo?: React.ReactNode;
}

/**
 * Build a mock FlowStepNode from theme values.
 * V2 editor sections read from content.canvas.background, content.header,
 * content.actionBar — we place theme values at those paths.
 */
function buildMockStep(theme: ThemeV2): FlowStepNode {
  return {
    id: 'theme-mock',
    entityId: 'theme-mock',
    screenType: 'HeroContent',
    content: {
      canvas: {
        background: theme.background,
        webViewBackgroundColor: theme.webViewBackgroundColor,
      },
      header: theme.headerBar,
      actionBar: theme.actionBar,
    },
  } as FlowStepNode;
}

/**
 * Path translation map: V2 editor section paths → theme-level paths.
 *
 * Editor sections emit paths like:
 *   content.canvas.background.kind → theme stores at background.kind
 *   content.header.back.* → theme stores at headerBar.back.*
 *   content.actionBar.primary.* → theme stores at actionBar.primary.*
 */
const PATH_TRANSLATIONS: Array<{ prefix: string; replacement: string }> = [
  { prefix: 'content.canvas.background.', replacement: 'background.' },
  { prefix: 'content.header.', replacement: 'headerBar.' },
  { prefix: 'content.actionBar.', replacement: 'actionBar.' },
];

function translatePath(editorPath: string): string {
  if (editorPath === 'content.canvas.webViewBackgroundColor') {
    return 'webViewBackgroundColor';
  }
  if (editorPath === 'content.canvas.background') {
    return 'background';
  }
  if (editorPath === 'content.header') {
    return 'headerBar';
  }
  if (editorPath === 'content.actionBar') {
    return 'actionBar';
  }

  for (const { prefix, replacement } of PATH_TRANSLATIONS) {
    if (editorPath.startsWith(prefix)) {
      return replacement + editorPath.slice(prefix.length);
    }
  }
  return editorPath;
}

/**
 * ThemeV2SettingsPanel - Container for all V2 theme settings sections
 */
export const ThemeV2SettingsPanel: React.FC<ThemeV2SettingsPanelProps> = ({
  theme,
  onChange,
  disabled = false,
  animationsDemo,
}) => {
  // Mock step for V2 editor sections (backdrop, header, action bar)
  const mockStep = useMemo(() => buildMockStep(theme), [theme]);

  // Translate V2 editor paths to theme-level paths
  const handleBackdropChange = useCallback(
    (path: string, value: unknown) => onChange(translatePath(path), value),
    [onChange]
  );

  const handleHeaderChange = useCallback(
    (path: string, value: unknown) => onChange(translatePath(path), value),
    [onChange]
  );

  const handleActionBarChange = useCallback(
    (path: string, value: unknown) => onChange(translatePath(path), value),
    [onChange]
  );

  return (
    <Box
      data-testid="theme-v2-settings-panel"
      aria-disabled={disabled}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {/* Colors Section */}
      <V2ColorsSection
        colors={theme.colors}
        onChange={onChange}
        disabled={disabled}
        defaultExpanded
      />

      {/* Fonts Section (V2 TypographyStyle) */}
      <V2FontsSection
        fonts={theme.fonts}
        onChange={onChange}
        disabled={disabled}
        defaultExpanded={false}
      />

      {/* Options List Section (replaces V1 TableSettingsSection) */}
      <V2OptionsListSection
        optionsListSettings={theme.optionsListSettings}
        onChange={onChange}
        disabled={disabled}
        defaultExpanded={false}
      />

      {/* Animations Section (motion scheme: transition + entrance) */}
      <V2AnimationsSection
        animationSettings={theme.animationSettings}
        onChange={onChange}
        disabled={disabled}
        defaultExpanded={false}
        transitionDemo={animationsDemo}
      />

      {/* Background Section (V2 Backdrop — solid/photo/clip) */}
      <V2BackdropSection
        step={mockStep}
        locale="en-US"
        onChange={handleBackdropChange}
        disabled={disabled}
        defaultExpanded={false}
        projectId="theme-v2-assets"
        onboardingId=""
        versionId=""
      />

      {/* Header Section (V2 HeaderBar — back/skip/progress) */}
      <V2HeaderBarSection
        step={mockStep}
        locale="en-US"
        onChange={handleHeaderChange}
        disabled={disabled}
        defaultExpanded={false}
        projectId="theme-v2-assets"
        onboardingId=""
        versionId=""
      />

      {/* Action Bar Section (V2 ActionBar — primary/secondary buttons) */}
      <V2ActionBarSection
        step={mockStep}
        locale="en-US"
        onChange={handleActionBarChange}
        disabled={disabled}
        defaultExpanded={false}
        projectId="theme-v2-assets"
        onboardingId=""
        versionId=""
      />
    </Box>
  );
};

export default ThemeV2SettingsPanel;
